// ============================================
// FILE: Entry_exit_log.js
// Entry/Exit Log Component - FIXED WITH STUDENT NAME
// ============================================

import React, { useState, useEffect } from 'react';

const EntryExitLog = ({ onNavigate, userInfo }) => {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [notificationsOpen, setNotificationsOpen] = useState(false);
    const [selectedWeek, setSelectedWeek] = useState(0);
    const [weekLogs, setWeekLogs] = useState([]);
    const [stats, setStats] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [studentName, setStudentName] = useState('');

    // API Base URL
    const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://app.learninghub.ind.in/api';

    // Today's date
    const TODAY = new Date();

    // Fetch weekly logs from backend
    useEffect(() => {
        const fetchWeeklyLogs = async () => {
            try {
                setLoading(true);
                setError(null);

                const userId = userInfo?.userId || userInfo?.id;

                if (!userId) {
                    throw new Error('User ID not found');
                }

                const token = localStorage.getItem('token') || sessionStorage.getItem('token');
                const headers = {
                    'Content-Type': 'application/json',
                };

                if (token) {
                    headers['Authorization'] = `Bearer ${token}`;
                }

                // Fetch student profile to get name
                const profileResponse = await fetch(
                    `${API_BASE_URL}/student/profile/${userId}`,
                    { method: 'GET', headers }
                );

                if (profileResponse.ok) {
                    const profileData = await profileResponse.json();
                    if (profileData.success && profileData.data) {
                        setStudentName(profileData.data.fullName || '');
                    }
                }

                // Fetch weekly logs
                const logsResponse = await fetch(
                    `${API_BASE_URL}/student/entry-exit-weekly/${userId}?weekOffset=${selectedWeek}`,
                    { method: 'GET', headers }
                );

                if (!logsResponse.ok) {
                    throw new Error('Failed to fetch weekly logs');
                }

                const logsData = await logsResponse.json();

                if (logsData.success) {
                    // Format the logs for display
                    const formattedLogs = logsData.weekLogs.map(day => ({
                        ...day,
                        entries: day.entries.map(entry => ({
                            time: formatTime(entry.time),
                            type: entry.type,
                            status: formatStatus(entry.status),
                            gate: entry.gate
                        }))
                    }));

                    setWeekLogs(formattedLogs);
                }

                // Fetch stats for current month
                const statsResponse = await fetch(
                    `${API_BASE_URL}/student/entry-exit-stats/${userId}`,
                    { method: 'GET', headers }
                );

                if (statsResponse.ok) {
                    const statsData = await statsResponse.json();
                    if (statsData.success) {
                        setStats([
                            {
                                icon: '🟢',
                                label: 'Total Entries',
                                value: (statsData.stats.totalEntries ?? 0).toString(),
                                subtext: 'This month'
                            },
                            {
                                icon: '🔴',
                                label: 'Total Exits',
                                value: (statsData.stats.totalExits ?? 0).toString(),
                                subtext: 'This month'
                            },
                            {
                                icon: '⚠️',
                                label: 'Late Entries',
                                value: (statsData.stats.lateEntries ?? 0).toString(),
                                subtext: 'This month'
                            },
                        ]);
                    }
                }

            } catch (err) {
                console.error('Error fetching entry/exit logs:', err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchWeeklyLogs();
    }, [selectedWeek, userInfo, API_BASE_URL]);

    // Helper function to format time from HH:MM:SS to 12-hour format
    const formatTime = (timeString) => {
        if (!timeString) return '';
        const [hours, minutes] = timeString.split(':');
        const hour = parseInt(hours);
        const ampm = hour >= 12 ? 'PM' : 'AM';
        const displayHour = hour % 12 || 12;
        return `${displayHour}:${minutes} ${ampm}`;
    };

    // Helper function to format status
    const formatStatus = (status) => {
        const statusMap = {
            'on-time': 'On Time',
            'late': 'Late Entry',
            'normal': 'Normal',
            'weekend': 'Weekend'
        };
        return statusMap[status] || status;
    };

    const notifications = [
        {
            id: 1,
            type: 'payment',
            title: 'Payment Reminder',
            message: 'Your hostel fee payment is due on Oct 15, 2025',
            time: '2h ago',
            icon: '💰',
            unread: true
        }
    ];

    const handleWeekChange = (direction) => {
        const newWeekOffset = selectedWeek + direction;
        setSelectedWeek(newWeekOffset);
    };

    const getCurrentWeekRange = () => {
        const currentDate = new Date(TODAY);
        const dayOfWeek = currentDate.getDay();
        const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
        const weekStart = new Date(currentDate);
        weekStart.setDate(currentDate.getDate() + diff + (selectedWeek * 7));
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6);

        return `${weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${weekEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
    };

    const MenuItem = ({ icon, label, active = false, route }) => (
        <div
            className={`menu-item ${active ? 'menu-item-active' : ''}`}
            onClick={() => {
                if (route && onNavigate) {
                    onNavigate(route);
                }
                setSidebarOpen(false);
            }}
        >
            <span className="menu-icon">{icon}</span>
            <span className="menu-label">{label}</span>
            <div className="menu-glow"></div>
        </div>
    );

    const handleLogout = () => {
        if (onNavigate) {
            onNavigate('hostel-login');
        }
    };

    // Loading State
    if (loading && weekLogs.length === 0) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                backgroundColor: '#ffffff',
                flexDirection: 'column',
                gap: '20px'
            }}>
                <div style={{
                    width: '60px',
                    height: '60px',
                    border: '6px solid #f3f3f3',
                    borderTop: '6px solid #2b6777',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                }}></div>
                <p style={{
                    color: '#2b6777',
                    fontSize: '18px',
                    fontWeight: '600'
                }}>Loading Entry/Exit Logs...</p>
                <style>{`
                    @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }
                `}</style>
            </div>
        );
    }

    // Error State
    if (error) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                backgroundColor: '#f4f7fa',
                padding: '2rem'
            }}>
                <div style={{
                    backgroundColor: 'white',
                    borderRadius: '16px',
                    padding: '3rem',
                    textAlign: 'center',
                    maxWidth: '500px',
                    boxShadow: '0 10px 25px rgba(0,0,0,0.1)'
                }}>
                    <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>⚠️</div>
                    <h3 style={{ color: '#dc2626', marginBottom: '1rem', fontSize: '1.5rem' }}>
                        Error Loading Logs
                    </h3>
                    <p style={{ color: '#666', marginBottom: '2rem' }}>{error}</p>
                    <button
                        onClick={() => window.location.reload()}
                        style={{
                            backgroundColor: '#2b6777',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            padding: '0.75rem 1.5rem',
                            cursor: 'pointer',
                            fontSize: '1rem',
                            fontWeight: '600'
                        }}
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');

                * {
                    margin: 0;
                    padding: 0;
                    box-sizing: border-box;
                }

                body {
                    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
                    -webkit-font-smoothing: antialiased;
                    overflow-x: hidden;
                }

                @keyframes slideIn {
                    from {
                        opacity: 0;
                        transform: translateY(30px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                @keyframes shimmer {
                    0% { background-position: -1000px 0; }
                    100% { background-position: 1000px 0; }
                }

                @keyframes pulse {
                    0%, 100% { transform: scale(1); }
                    50% { transform: scale(1.05); }
                }

                @keyframes glow {
                    0%, 100% { opacity: 0.5; }
                    50% { opacity: 1; }
                }

                .dashboard-container {
                    display: flex;
                    height: 100vh;
                    background: #ffffff;
                    position: relative;
                    overflow: hidden;
                }

                /* Sidebar */
                .sidebar {
                    position: fixed;
                    top: 0;
                    left: 0;
                    bottom: 0;
                    width: 300px;
                    background: linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(242,242,242,0.98) 100%);
                    backdrop-filter: blur(30px);
                    border-right: 2px solid rgba(82,171,152,0.2);
                    transform: translateX(-100%);
                    transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
                    z-index: 100;
                    display: flex;
                    flex-direction: column;
                    box-shadow: 8px 0 40px rgba(43,103,119,0.15);
                }

                .sidebar.open {
                    transform: translateX(0);
                }

                .sidebar-header {
                    padding: 36px 28px;
                    background: linear-gradient(135deg, rgba(43,103,119,0.05) 0%, rgba(82,171,152,0.05) 100%);
                    border-bottom: 2px solid rgba(82,171,152,0.15);
                    position: relative;
                    overflow: hidden;
                }

                .sidebar-header::before {
                    content: '';
                    position: absolute;
                    top: 0;
                    left: -100%;
                    width: 100%;
                    height: 100%;
                    background: linear-gradient(90deg, transparent, rgba(82,171,152,0.15), transparent);
                    animation: shimmer 4s infinite;
                }

                .sidebar-top {
                    display: flex;
                    align-items: center;
                    justify-content: flex-end;
                    margin-bottom: 24px;
                    position: relative;
                    z-index: 1;
                }

                .close-btn {
                    background: rgba(43,103,119,0.08);
                    border: 1.5px solid rgba(82,171,152,0.25);
                    cursor: pointer;
                    font-size: 20px;
                    color: #2b6777;
                    width: 38px;
                    height: 38px;
                    border-radius: 10px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.3s;
                }

                .close-btn:hover {
                    background: rgba(43,103,119,0.15);
                    border-color: #52ab98;
                    transform: rotate(90deg);
                }

                .user-info-sidebar {
                    display: flex;
                    align-items: center;
                    gap: 14px;
                    position: relative;
                    z-index: 1;
                }

                .user-avatar-sidebar {
                    width: 56px;
                    height: 56px;
                    border-radius: 50%;
                    background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-weight: 800;
                    font-size: 22px;
                    color: white;
                    box-shadow: 0 6px 24px rgba(43,103,119,0.3);
                    border: 3px solid rgba(255, 255, 255, 0.5);
                }

                .user-details-sidebar {
                    flex: 1;
                }

                .user-name-sidebar {
                    font-size: 16px;
                    font-weight: 800;
                    color: #2b6777;
                    margin-bottom: 4px;
                }

                .user-role-sidebar {
                    font-size: 13px;
                    color: #52ab98;
                    font-weight: 600;
                }

                .sidebar-nav {
                    flex: 1;
                    padding: 28px 20px;
                    overflow-y: auto;
                }

                .sidebar-nav::-webkit-scrollbar {
                    width: 6px;
                }

                .sidebar-nav::-webkit-scrollbar-track {
                    background: rgba(82,171,152,0.08);
                }

                .sidebar-nav::-webkit-scrollbar-thumb {
                    background: rgba(82,171,152,0.3);
                    border-radius: 3px;
                }

                .menu-item {
                    position: relative;
                    display: flex;
                    align-items: center;
                    gap: 14px;
                    padding: 16px 18px;
                    margin-bottom: 10px;
                    cursor: pointer;
                    color: #2b6777;
                    font-size: 15px;
                    font-weight: 600;
                    border-radius: 14px;
                    transition: all 0.3s;
                    overflow: hidden;
                }

                .menu-glow {
                    position: absolute;
                    inset: 0;
                    background: linear-gradient(135deg, rgba(43,103,119,0.08) 0%, rgba(82,171,152,0.08) 100%);
                    opacity: 0;
                    transition: opacity 0.3s;
                }

                .menu-item:hover {
                    color: #52ab98;
                    background: rgba(82,171,152,0.1);
                    transform: translateX(6px);
                    box-shadow: 0 4px 16px rgba(82,171,152,0.15);
                }

                .menu-item:hover .menu-glow {
                    opacity: 1;
                }

                .menu-item-active {
                    background: linear-gradient(135deg, rgba(43,103,119,0.15) 0%, rgba(82,171,152,0.15) 100%);
                    color: #2b6777;
                    box-shadow: 0 6px 20px rgba(82,171,152,0.2);
                    font-weight: 700;
                }

                .menu-item-active .menu-glow {
                    opacity: 1;
                    animation: glow 2s ease-in-out infinite;
                }

                .menu-icon {
                    font-size: 22px;
                    position: relative;
                    z-index: 1;
                }

                .menu-label {
                    position: relative;
                    z-index: 1;
                }

                .logout-section {
                    padding: 20px;
                    border-top: 2px solid rgba(82,171,152,0.15);
                }

                .logout-btn {
                    width: 100%;
                    padding: 16px 20px;
                    background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
                    color: white;
                    border: none;
                    border-radius: 14px;
                    font-size: 15px;
                    font-weight: 700;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 10px;
                    transition: all 0.3s;
                    box-shadow: 0 6px 24px rgba(43,103,119,0.3);
                }

                .logout-btn:hover {
                    transform: translateY(-3px);
                    box-shadow: 0 10px 35px rgba(43,103,119,0.4);
                }

                /* Overlay */
                .overlay {
                    position: fixed;
                    inset: 0;
                    background: rgba(43,103,119,0.4);
                    backdrop-filter: blur(6px);
                    z-index: 90;
                    opacity: 0;
                    visibility: hidden;
                    transition: all 0.3s ease;
                }

                .overlay.show {
                    opacity: 1;
                    visibility: visible;
                }

                .overlay.notifications-overlay {
                    z-index: 999;
                }

                /* Main Content */
                .main-content {
                    flex: 1;
                    display: flex;
                    flex-direction: column;
                    overflow: hidden;
                    width: 100%;
                    position: relative;
                    z-index: 1;
                }

                /* Top Header */
                .top-header {
                    background: #ffffff;
                    padding: 24px 20px;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    border-bottom: 1px solid rgba(82,171,152,0.12);
                    position: relative;
                    box-shadow: 0 2px 8px rgba(43,103,119,0.06);
                }

                .top-header::after {
                    content: '';
                    position: absolute;
                    bottom: 0;
                    left: 0;
                    right: 0;
                    height: 3px;
                    background: linear-gradient(90deg, #2b6777 0%, #52ab98 100%);
                }

                .header-left {
                    display: flex;
                    align-items: center;
                    gap: 16px;
                    flex: 1;
                }

                .menu-toggle {
                    background: linear-gradient(135deg, rgba(43,103,119,0.1) 0%, rgba(82,171,152,0.1) 100%);
                    border: 2px solid rgba(82,171,152,0.25);
                    cursor: pointer;
                    font-size: 24px;
                    color: #2b6777;
                    width: 44px;
                    height: 44px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.3s;
                    box-shadow: 0 4px 20px rgba(43,103,119,0.15);
                    flex-shrink: 0;
                }

                .menu-toggle:hover {
                    transform: scale(1.05);
                    box-shadow: 0 6px 30px rgba(43,103,119,0.25);
                    background: linear-gradient(135deg, rgba(43,103,119,0.15) 0%, rgba(82,171,152,0.15) 100%);
                }

                .header-title-section {
                    display: flex;
                    flex-direction: column;
                    gap: 4px;
                    min-width: 0;
                    flex: 1;
                }

                .header-greeting {
                    font-size: 13px;
                    color: #52ab98;
                    font-weight: 600;
                    display: none;
                }

                .header-name {
                    font-size: 18px;
                    font-weight: 900;
                    background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                    background-clip: text;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }

                .header-right {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    flex-shrink: 0;
                }

                .notification-btn {
                    position: relative;
                    background: rgba(82,171,152,0.1);
                    border: 2px solid rgba(82,171,152,0.2);
                    cursor: pointer;
                    font-size: 20px;
                    color: #2b6777;
                    width: 44px;
                    height: 44px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.3s;
                }

                .notification-btn:hover,
                .notification-btn.active {
                    background: rgba(82,171,152,0.2);
                    border-color: #52ab98;
                    box-shadow: 0 6px 24px rgba(82,171,152,0.3);
                    transform: scale(1.05);
                }

                .notification-badge {
                    position: absolute;
                    top: 4px;
                    right: 4px;
                    min-width: 20px;
                    height: 20px;
                    background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
                    border-radius: 10px;
                    font-size: 10px;
                    font-weight: 800;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 0 5px;
                    color: white;
                    border: 2px solid white;
                    box-shadow: 0 3px 12px rgba(43,103,119,0.4);
                    animation: pulse 2s infinite;
                }

                .user-avatar-header {
                    width: 44px;
                    height: 44px;
                    border-radius: 50%;
                    background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-weight: 800;
                    font-size: 18px;
                    color: white;
                    cursor: pointer;
                    border: 3px solid rgba(82,171,152,0.3);
                    transition: all 0.3s;
                    box-shadow: 0 6px 24px rgba(43,103,119,0.3);
                }

                .user-avatar-header:hover {
                    transform: scale(1.1);
                    box-shadow: 0 8px 32px rgba(43,103,119,0.5);
                }

                /* Notifications Panel */
                .notifications-panel {
                    position: fixed;
                    top: 80px;
                    right: 20px;
                    width: 380px;
                    max-width: calc(100vw - 40px);
                    max-height: 600px;
                    background: rgba(255,255,255,0.98);
                    backdrop-filter: blur(40px);
                    border-radius: 20px;
                    box-shadow: 0 24px 70px rgba(43,103,119,0.25);
                    z-index: 1000;
                    opacity: 0;
                    visibility: hidden;
                    transform: translateY(-20px);
                    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
                    border: 2px solid rgba(82,171,152,0.2);
                    overflow: hidden;
                }

                .notifications-panel.open {
                    opacity: 1;
                    visibility: visible;
                    transform: translateY(0);
                }

                /* Content Wrapper */
                .content-wrapper {
                    flex: 1;
                    overflow-y: auto;
                    padding: 20px 16px;
                }

                .content-wrapper::-webkit-scrollbar {
                    width: 8px;
                }

                .content-wrapper::-webkit-scrollbar-track {
                    background: rgba(82,171,152,0.08);
                }

                .content-wrapper::-webkit-scrollbar-thumb {
                    background: rgba(82,171,152,0.3);
                    border-radius: 4px;
                }

                .content-area {
                    max-width: 1500px;
                    margin: 0 auto;
                    animation: slideIn 0.6s ease-out;
                }

                /* Hero Section */
                .hero-section {
                    background: linear-gradient(135deg, rgba(43,103,119,0.08) 0%, rgba(82,171,152,0.08) 100%);
                    border-radius: 12px;
                    padding: 28px 20px;
                    margin-bottom: 24px;
                    border: 1px solid rgba(82,171,152,0.15);
                    box-shadow: 0 2px 8px rgba(43,103,119,0.06);
                    position: relative;
                    overflow: hidden;
                }

                .hero-section::before {
                    content: '';
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    height: 4px;
                    background: linear-gradient(90deg, #2b6777 0%, #52ab98 100%);
                }

                .hero-content {
                    position: relative;
                    z-index: 1;
                }

                .hero-title {
                    font-size: 24px;
                    font-weight: 700;
                    color: #2b6777;
                    margin-bottom: 8px;
                    letter-spacing: -0.5px;
                }

                .hero-subtitle {
                    font-size: 13px;
                    color: rgba(43,103,119,0.6);
                    font-weight: 500;
                    line-height: 1.4;
                }

                /* Stats Grid */
                .stats-grid {
                    display: grid;
                    grid-template-columns: repeat(2, 1fr);
                    gap: 12px;
                    margin-bottom: 24px;
                }

                .stat-card {
                    background: #ffffff;
                    border-radius: 12px;
                    padding: 20px 16px;
                    border: 1px solid rgba(43,103,119,0.12);
                    cursor: pointer;
                    transition: all 0.3s ease;
                    position: relative;
                    overflow: hidden;
                    box-shadow: 0 2px 8px rgba(43,103,119,0.06);
                }

                .stat-card::before {
                    content: '';
                    position: absolute;
                    top: 0;
                    left: 0;
                    width: 4px;
                    height: 100%;
                    background: linear-gradient(180deg, #2b6777 0%, #52ab98 100%);
                    transform: scaleY(0);
                    transition: transform 0.3s ease;
                }

                .stat-card:hover {
                    transform: translateY(-4px);
                    box-shadow: 0 8px 24px rgba(43,103,119,0.12);
                    border-color: rgba(82,171,152,0.25);
                }

                .stat-card:hover::before {
                    transform: scaleY(1);
                }

                .stat-header {
                    display: flex;
                    align-items: flex-start;
                    justify-content: space-between;
                    margin-bottom: 16px;
                }

                .stat-label {
                    font-size: 10px;
                    color: rgba(43,103,119,0.6);
                    font-weight: 600;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }

                .stat-icon {
                    width: 36px;
                    height: 36px;
                    border-radius: 8px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 18px;
                    background: rgba(82,171,152,0.08);
                    border: 1px solid rgba(82,171,152,0.15);
                }

                .stat-value {
                    font-size: 26px;
                    font-weight: 700;
                    color: #2b6777;
                    margin-bottom: 6px;
                    letter-spacing: -0.5px;
                    line-height: 1;
                }

                .stat-subtext {
                    font-size: 11px;
                    color: rgba(43,103,119,0.5);
                    font-weight: 500;
                }

                /* Week Navigation */
                .week-nav-section {
                    background: rgba(255,255,255,0.9);
                    backdrop-filter: blur(30px);
                    border-radius: 16px;
                    padding: 20px 16px;
                    margin-bottom: 24px;
                    border: 2px solid rgba(82,171,152,0.2);
                    box-shadow: 0 8px 32px rgba(43,103,119,0.12);
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    flex-wrap: wrap;
                    gap: 16px;
                }

                .week-info {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    flex: 1;
                    min-width: 0;
                }

                .week-icon {
                    width: 48px;
                    height: 48px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 24px;
                    background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
                    color: white;
                    box-shadow: 0 6px 24px rgba(43,103,119,0.3);
                    flex-shrink: 0;
                }

                .week-text {
                    flex: 1;
                    min-width: 0;
                }

                .week-label {
                    font-size: 11px;
                    color: rgba(43,103,119,0.6);
                    font-weight: 600;
                    margin-bottom: 4px;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }

                .week-range {
                    font-size: 16px;
                    font-weight: 900;
                    color: #2b6777;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                }

                .week-controls {
                    display: flex;
                    gap: 8px;
                    flex-shrink: 0;
                }

                .week-btn {
                    background: rgba(82,171,152,0.1);
                    border: 2px solid rgba(82,171,152,0.25);
                    cursor: pointer;
                    font-size: 18px;
                    color: #2b6777;
                    width: 40px;
                    height: 40px;
                    border-radius: 10px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.3s;
                    font-weight: 800;
                }

                .week-btn:hover {
                    background: rgba(82,171,152,0.2);
                    border-color: #52ab98;
                    transform: scale(1.05);
                    box-shadow: 0 4px 20px rgba(82,171,152,0.3);
                }

                .week-btn:disabled {
                    opacity: 0.4;
                    cursor: not-allowed;
                }

                .week-btn:disabled:hover {
                    transform: none;
                    box-shadow: none;
                }

                /* Weekly Logs */
                .weekly-logs-grid {
                    display: grid;
                    gap: 16px;
                }

                .day-log-card {
                    background: rgba(255,255,255,0.9);
                    backdrop-filter: blur(30px);
                    border-radius: 16px;
                    padding: 20px 16px;
                    border: 2px solid rgba(82,171,152,0.2);
                    box-shadow: 0 8px 32px rgba(43,103,119,0.12);
                    transition: all 0.3s;
                }

                .day-log-card.today {
                    background: linear-gradient(135deg, rgba(43,103,119,0.08) 0%, rgba(82,171,152,0.08) 100%);
                    border: 2px solid rgba(82,171,152,0.4);
                    box-shadow: 0 12px 40px rgba(43,103,119,0.2);
                }

                .day-log-card.future {
                    opacity: 0.5;
                    pointer-events: none;
                }

                .day-log-card:hover {
                    transform: translateY(-4px);
                    box-shadow: 0 12px 40px rgba(43,103,119,0.18);
                    border-color: rgba(82,171,152,0.3);
                }

                .day-header {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    margin-bottom: 16px;
                    padding-bottom: 16px;
                    border-bottom: 2px solid rgba(82,171,152,0.15);
                    gap: 12px;
                }

                .day-info {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    flex: 1;
                    min-width: 0;
                }

                .day-icon-wrapper {
                    width: 44px;
                    height: 44px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 22px;
                    background: linear-gradient(135deg, rgba(43,103,119,0.1) 0%, rgba(82,171,152,0.1) 100%);
                    border: 2px solid rgba(82,171,152,0.25);
                    flex-shrink: 0;
                }

                .day-text {
                    flex: 1;
                    min-width: 0;
                }

                .day-name {
                    font-size: 16px;
                    font-weight: 900;
                    color: #2b6777;
                    margin-bottom: 4px;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                }

                .day-date {
                    font-size: 11px;
                    color: rgba(43,103,119,0.6);
                    font-weight: 600;
                }

                .day-badge {
                    background: linear-gradient(135deg, rgba(43,103,119,0.1) 0%, rgba(82,171,152,0.1) 100%);
                    border: 1.5px solid rgba(82,171,152,0.25);
                    color: #2b6777;
                    font-size: 10px;
                    font-weight: 800;
                    padding: 6px 12px;
                    border-radius: 8px;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    white-space: nowrap;
                    flex-shrink: 0;
                }

                .day-badge.today-badge {
                    background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
                    border: 1.5px solid transparent;
                    color: white;
                    box-shadow: 0 4px 16px rgba(43,103,119,0.3);
                }

                .day-badge.future-badge {
                    background: rgba(0,0,0,0.05);
                    border: 1.5px solid rgba(0,0,0,0.1);
                    color: rgba(0,0,0,0.3);
                }

                .entries-list {
                    display: grid;
                    gap: 12px;
                }

                .entry-item {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    padding: 16px 12px;
                    background: rgba(82,171,152,0.05);
                    border-radius: 12px;
                    border: 1.5px solid rgba(82,171,152,0.15);
                    transition: all 0.3s;
                }

                .entry-item:hover {
                    background: rgba(82,171,152,0.1);
                    border-color: rgba(82,171,152,0.25);
                    transform: translateX(4px);
                }

                .entry-indicator {
                    width: 40px;
                    height: 40px;
                    border-radius: 10px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 20px;
                    flex-shrink: 0;
                }

                .entry-indicator.entry {
                    background: rgba(76, 175, 80, 0.15);
                    border: 2px solid rgba(76, 175, 80, 0.3);
                    color: #4caf50;
                }

                .entry-indicator.exit {
                    background: rgba(244, 67, 54, 0.15);
                    border: 2px solid rgba(244, 67, 54, 0.3);
                    color: #f44336;
                }

                .entry-details {
                    flex: 1;
                    min-width: 0;
                }

                .entry-time {
                    font-size: 16px;
                    font-weight: 800;
                    color: #2b6777;
                    margin-bottom: 6px;
                }

                .entry-meta {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    font-size: 11px;
                    color: rgba(43,103,119,0.6);
                    font-weight: 600;
                    flex-wrap: wrap;
                }

                .entry-gate {
                    display: flex;
                    align-items: center;
                    gap: 4px;
                }

                .entry-status {
                    padding: 4px 8px;
                    border-radius: 6px;
                    font-size: 10px;
                    font-weight: 800;
                    text-transform: uppercase;
                    letter-spacing: 0.3px;
                    white-space: nowrap;
                }

                .entry-status.on-time {
                    background: rgba(76, 175, 80, 0.15);
                    color: #4caf50;
                    border: 1.5px solid rgba(76, 175, 80, 0.3);
                }

                .entry-status.late-entry {
                    background: rgba(255, 152, 0, 0.15);
                    color: #ff9800;
                    border: 1.5px solid rgba(255, 152, 0, 0.3);
                }

                .entry-status.normal {
                    background: rgba(33, 150, 243, 0.15);
                    color: #2196f3;
                    border: 1.5px solid rgba(33, 150, 243, 0.3);
                }

                .entry-status.weekend {
                    background: rgba(156, 39, 176, 0.15);
                    color: #9c27b0;
                    border: 1.5px solid rgba(156, 39, 176, 0.3);
                }

                /* Responsive - Tablet */
                @media (min-width: 768px) {
                    .top-header {
                        padding: 32px 24px;
                    }

                    .header-name {
                        font-size: 22px;
                    }

                    .header-greeting {
                        display: block;
                        font-size: 14px;
                    }

                    .header-title-section {
                        flex-direction: row;
                        align-items: center;
                        gap: 12px;
                    }

                    .content-wrapper {
                        padding: 32px 24px;
                    }

                    .hero-section {
                        padding: 48px 28px;
                    }

                    .hero-title {
                        font-size: 32px;
                    }

                    .hero-subtitle {
                        font-size: 15px;
                    }

                    .stats-grid {
                        grid-template-columns: repeat(4, 1fr);
                        gap: 16px;
                    }

                    .stat-card {
                        padding: 24px 20px;
                    }

                    .week-nav-section {
                        padding: 24px 28px;
                        flex-wrap: nowrap;
                    }

                    .week-range {
                        font-size: 20px;
                    }
                }

                /* Responsive - Desktop */
                @media (min-width: 1024px) {
                    .sidebar {
                        position: static;
                        transform: translateX(0);
                    }

                    .close-btn {
                        display: none;
                    }

                    .overlay {
                        display: none !important;
                    }

                    .menu-toggle {
                        display: none;
                    }

                    .top-header {
                        padding: 52px 32px;
                    }

                    .header-name {
                        font-size: 28px;
                    }

                    .header-greeting {
                        font-size: 18px;
                    }

                    .content-wrapper {
                        padding: 56px 48px;
                    }

                    .hero-section {
                        padding: 90px 36px;
                    }

                    .hero-title {
                        font-size: 36px;
                    }

                    .hero-subtitle {
                        font-size: 16px;
                    }

                    .stats-grid {
                        gap: 20px;
                    }

                    .stat-card {
                        padding: 28px;
                    }

                    .stat-label {
                        font-size: 12px;
                    }

                    .stat-icon {
                        width: 48px;
                        height: 48px;
                        font-size: 24px;
                    }

                    .stat-value {
                        font-size: 32px;
                    }

                    .stat-subtext {
                        font-size: 13px;
                    }

                    .week-nav-section {
                        padding: 32px 40px;
                    }

                    .week-icon {
                        width: 64px;
                        height: 64px;
                        font-size: 32px;
                        border-radius: 18px;
                    }

                    .week-label {
                        font-size: 14px;
                    }

                    .week-range {
                        font-size: 24px;
                    }

                    .week-controls {
                        gap: 12px;
                    }

                    .week-btn {
                        width: 48px;
                        height: 48px;
                        font-size: 20px;
                        border-radius: 12px;
                    }

                    .weekly-logs-grid {
                        gap: 24px;
                    }

                    .day-log-card {
                        padding: 32px;
                        border-radius: 24px;
                    }

                    .day-header {
                        margin-bottom: 24px;
                        padding-bottom: 20px;
                    }

                    .day-icon-wrapper {
                        width: 56px;
                        height: 56px;
                        font-size: 28px;
                        border-radius: 14px;
                    }

                    .day-info {
                        gap: 16px;
                    }

                    .day-name {
                        font-size: 22px;
                    }

                    .day-date {
                        font-size: 14px;
                    }

                    .day-badge {
                        font-size: 12px;
                        padding: 8px 16px;
                        border-radius: 10px;
                    }

                    .entries-list {
                        gap: 16px;
                    }

                    .entry-item {
                        padding: 20px;
                        gap: 20px;
                        border-radius: 16px;
                    }

                    .entry-indicator {
                        width: 48px;
                        height: 48px;
                        font-size: 24px;
                        border-radius: 12px;
                    }

                    .entry-time {
                        font-size: 18px;
                    }

                    .entry-meta {
                        font-size: 13px;
                        gap: 12px;
                    }

                    .entry-status {
                        padding: 6px 12px;
                        font-size: 12px;
                        border-radius: 8px;
                    }
                }

                /* Small mobile adjustments */
                @media (max-width: 380px) {
                    .header-name {
                        font-size: 16px;
                    }

                    .hero-title {
                        font-size: 20px;
                    }

                    .stat-value {
                        font-size: 22px;
                    }

                    .week-range {
                        font-size: 14px;
                    }

                    .day-name {
                        font-size: 14px;
                    }
                }
            `}</style>

            <div className="dashboard-container">
                {/* Sidebar */}
                <div className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
                    <div className="sidebar-header">
                        <div className="sidebar-top">
                            <button onClick={() => setSidebarOpen(false)} className="close-btn">
                                ✕
                            </button>
                        </div>
                        <div className="user-info-sidebar">
                            <div className="user-avatar-sidebar">
                                {studentName ? studentName.charAt(0).toUpperCase() : 'S'}
                            </div>
                            <div className="user-details-sidebar">
                                <div className="user-name-sidebar">{studentName || 'Student Name'}</div>
                                <div className="user-role-sidebar">Student Portal</div>
                            </div>
                        </div>
                    </div>

                    <nav className="sidebar-nav">
                        <MenuItem icon="🏠" label="Dashboard" route="hostel-student-dashboard" />
                        <MenuItem icon="🚪" label="Entry/Exit Log" active={true} route="hostel-student-entry-exit-log" />
                        <MenuItem icon="🛏" label="Room Details" route="hostel-student-room-details" />
                        <MenuItem icon="🍽️" label="Food Preference" route="hostel-student-food-preference" />
                        <MenuItem icon="🧺" label="Laundry Service" route="hostel-student-laundry" />
                        <MenuItem icon="💳" label="Grievance" route="hostel-student-payments" />
                        <MenuItem icon="📝" label="Requests" route="hostel-student-requests" />
                        <MenuItem icon="📋" label="Requisition" route="hostel-student-requisition" />
                    </nav>

                    <div className="logout-section">
                        <button onClick={handleLogout} className="logout-btn">
                            🚪 Logout
                        </button>
                    </div>
                </div>

                {/* Overlays */}
                <div
                    className={`overlay ${sidebarOpen ? 'show' : ''}`}
                    onClick={() => setSidebarOpen(false)}
                />
                <div
                    className={`overlay notifications-overlay ${notificationsOpen ? 'show' : ''}`}
                    onClick={() => setNotificationsOpen(false)}
                />

                {/* Main Content */}
                <div className="main-content">
                    {/* Top Header */}
                    <div className="top-header">
                        <div className="header-left">
                            <button
                                className="menu-toggle"
                                onClick={() => setSidebarOpen(true)}
                            >
                                ☰
                            </button>
                            <div className="header-title-section">
                                <div className="header-greeting">Entry & Exit Logs</div>
                                <div className="header-name">{studentName || 'Student'}</div>
                            </div>
                        </div>
                        <div className="header-right">
                            <div className="user-avatar-header">
                                {studentName ? studentName.charAt(0).toUpperCase() : 'S'}
                            </div>
                        </div>
                    </div>

                    {/* Content */}
                    <div className="content-wrapper">
                        <div className="content-area">
                            {/* Hero Section */}
                            <div className="hero-section">
                                <div className="hero-content">
                                    <h1 className="hero-title">Entry & Exit Logs 🚪</h1>
                                    <p className="hero-subtitle">Track your daily hostel check-ins and check-outs • Today: {TODAY.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
                                </div>
                            </div>

                            {/* Stats Grid */}
                            {stats.length > 0 && (
                                <div className="stats-grid">
                                    {stats.map((stat, index) => (
                                        <div key={index} className="stat-card">
                                            <div className="stat-header">
                                                <div className="stat-label">{stat.label}</div>
                                                <div className="stat-icon">{stat.icon}</div>
                                            </div>
                                            <div className="stat-value">{stat.value}</div>
                                            <div className="stat-subtext">{stat.subtext}</div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Week Navigation */}
                            <div className="week-nav-section">
                                <div className="week-info">
                                    <div className="week-icon">📅</div>
                                    <div className="week-text">
                                        <div className="week-label">{selectedWeek === 0 ? 'This Week' : `${Math.abs(selectedWeek)} ${Math.abs(selectedWeek) === 1 ? 'Week' : 'Weeks'} Ago`}</div>
                                        <div className="week-range">{getCurrentWeekRange()}</div>
                                    </div>
                                </div>
                                <div className="week-controls">
                                    <button
                                        className="week-btn"
                                        onClick={() => handleWeekChange(-1)}
                                    >
                                        ‹
                                    </button>
                                    <button
                                        className="week-btn"
                                        onClick={() => handleWeekChange(1)}
                                        disabled={selectedWeek >= 0}
                                    >
                                        ›
                                    </button>
                                </div>
                            </div>

                            {/* Weekly Logs */}
                            <div className="weekly-logs-grid">
                                {weekLogs.map((day, index) => (
                                    <div key={index} className={`day-log-card ${day.isToday ? 'today' : ''} ${day.isFuture ? 'future' : ''}`}>
                                        <div className="day-header">
                                            <div className="day-info">
                                                <div className="day-icon-wrapper">
                                                    {day.isToday ? '⭐' : day.isFuture ? '📅' : '📆'}
                                                </div>
                                                <div className="day-text">
                                                    <div className="day-name">{day.day}</div>
                                                    <div className="day-date">{day.date}</div>
                                                </div>
                                            </div>
                                            <div className={`day-badge ${day.isToday ? 'today-badge' : ''} ${day.isFuture ? 'future-badge' : ''}`}>
                                                {day.isToday ? 'Today' : day.isFuture ? 'Upcoming' : `${day.entries.length} Logs`}
                                            </div>
                                        </div>

                                        {day.entries.length > 0 ? (
                                            <div className="entries-list">
                                                {day.entries.map((entry, entryIndex) => (
                                                    <div key={entryIndex} className="entry-item">
                                                        <div className={`entry-indicator ${entry.type}`}>
                                                            {entry.type === 'entry' ? '↑' : '↓'}
                                                        </div>
                                                        <div className="entry-details">
                                                            <div className="entry-time">{entry.time}</div>
                                                            <div className="entry-meta">
                                                                <div className="entry-gate">
                                                                    <span>🚪</span>
                                                                    <span>{entry.gate}</span>
                                                                </div>
                                                                <span>•</span>
                                                                <span className={`entry-status ${entry.status.toLowerCase().replace(' ', '-')}`}>
                                                                    {entry.status}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <div style={{
                                                textAlign: 'center',
                                                padding: '40px 20px',
                                                color: 'rgba(43,103,119,0.4)',
                                                fontSize: '14px',
                                                fontWeight: '600'
                                            }}>
                                                {day.isFuture ? 'No data available for future dates' : 'No entries recorded'}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default EntryExitLog;